import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ArrowLeft, Plus, Trash2, Edit2, Check, Save, User, UserPlus, Loader2, TrendingUp } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { ShirtIcon } from "../components/ShirtIcon";

// Exact copy of AdminSquad.jsx, pointed at the fully independent
// `classic_squad` collection used by the Classic Lineup Builder.
export default function AdminClassicSquad() {
  const [squad, setSquad] = useState([]);
  const [shirtDesigns, setShirtDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Edit / Add Form State
  const [editingId, setEditingId] = useState(null); // null = adding, string = editing ID
  const [name, setName] = useState("");
  const [positionType, setPositionType] = useState("outfield");
  const [shirtDesignId, setShirtDesignId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [squadRes, designsRes] = await Promise.all([
        api.get("/admin/classic-squad"),
        api.get("/classic-shirt-designs"),
      ]);
      setSquad(squadRes.data || []);
      // Only outfield designs — this per-player default never applies to
      // the goalkeeper slot (see ClassicLineupBuilderPage.jsx), so a
      // goalkeeper-only design would never actually be used here.
      setShirtDesigns((designsRes.data || []).filter((d) => (d.role || "outfield") !== "goalkeeper"));
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setPositionType("outfield");
    setShirtDesignId("");
    setError("");
  };

  const startEdit = (player) => {
    setEditingId(player.id);
    setName(player.name);
    setPositionType(player.position_type || "outfield");
    setShirtDesignId(player.shirt_design_id || "");
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Please fill in the Name field");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        name: name.trim(),
        position_type: positionType,
        shirt_design_id: shirtDesignId || null,
      };
      if (editingId) {
        await api.put(`/admin/classic-squad/${editingId}`, payload);
        setSuccess("Player updated successfully!");
      } else {
        await api.post("/admin/classic-squad", payload);
        setSuccess("Player added successfully!");
      }
      resetForm();
      await load();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (playerId) => {
    if (!window.confirm("Are you sure you want to remove this player from the Classic squad list?")) return;
    setError("");
    setSuccess("");
    try {
      await api.delete(`/admin/classic-squad/${playerId}`);
      setSuccess("Player removed successfully!");
      await load();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[var(--brand-ink)]">
      <Header />

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--brand-body)] uppercase tracking-wider hover:text-[var(--brand-ink)] mb-1"
            >
              <ArrowLeft size={12} /> Back to dashboard
            </Link>
            <h1 className="font-display text-2xl font-bold text-[var(--brand-ink)]">
              Classic Squad Manager
            </h1>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Add, edit, or remove players in the Classic squad list used by the Classic Lineup Builder.
            </p>
          </div>
        </div>

        {/* Form panel */}
        <form onSubmit={handleSubmit} className="mb-8 rounded-md border border-black/10 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            {editingId ? <Edit2 size={16} className="text-[#6CABDD]" /> : <UserPlus size={16} className="text-[#6CABDD]" />}
            {editingId ? "Edit Player Details" : "Add New Classic Squad Player"}
          </h2>

          {error ? (
            <p className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm text-[var(--brand-ink)]">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="mb-4 rounded-sm border-l-2 border-emerald-500 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-700 font-bold">
              {success}
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Player Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Dion Dublin"
                className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              />
            </div>
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Position Type</label>
              <select
                value={positionType}
                onChange={(e) => setPositionType(e.target.value)}
                className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              >
                <option value="outfield">Outfield Player</option>
                <option value="goalkeeper">Goalkeeper</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block label-kicker text-[var(--brand-body)]">
              Default shirt (optional)
            </label>
            <p className="mt-0.5 text-xs text-[var(--brand-body)]">
              Shows automatically in the Classic Lineup Builder whenever this
              player is placed in a slot — falls back to the design picked in
              the builder's own dropdown if left blank. Never applies to the
              goalkeeper slot.
            </p>
            <div className="mt-2 flex items-center gap-3">
              <ShirtIcon
                isGk={false}
                sizeClass="h-12 w-12"
                showNumber={false}
                design={shirtDesigns.find((d) => d.id === shirtDesignId) || "classic"}
              />
              <select
                value={shirtDesignId}
                onChange={(e) => setShirtDesignId(e.target.value)}
                className="rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
                data-testid="classic-squad-shirt-select"
              >
                <option value="">No default (use builder's dropdown)</option>
                {shirtDesigns.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between border-t border-black/5 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-black disabled:opacity-40"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {editingId ? "Save changes" : "Add Player"}
            </button>
            {editingId ? (
              <button
                type="button"
                onClick={resetForm}
                className="label-kicker text-[var(--brand-body)] underline underline-offset-4 hover:text-[var(--brand-ink)]"
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>

        {/* Most Selected leaderboard — includes a one-time backdated tally
            from historical shared lineups since the Classic Builder went
            live (2026-09-06), on top of live ticks from the builder. */}
        <div className="mt-8 rounded-md border border-black/10 bg-white p-5 shadow-sm" data-testid="classic-squad-most-selected">
          <h2 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
            <TrendingUp size={16} className="text-[#6CABDD]" />
            Most Selected
          </h2>
          <p className="mb-4 text-sm text-[var(--brand-body)]">
            How many times each legend has been picked in the Classic Lineup Builder, most-picked first.
          </p>
          {loading ? (
            <div className="h-16 animate-pulse rounded-md border border-black/10 bg-[#F8FAFC]" />
          ) : squad.length === 0 ? (
            <p className="text-sm text-[var(--brand-body)] italic">No players yet.</p>
          ) : (
            <div className="divide-y divide-black/5">
              {[...squad]
                .sort((a, b) => (b.selection_count || 0) - (a.selection_count || 0))
                .map((p, idx) => (
                  <div key={p.id} className="flex items-center gap-3 py-2.5" data-testid={`classic-squad-selection-row-${p.id}`}>
                    <span className="w-6 shrink-0 text-right text-xs font-bold text-[var(--brand-body)]">{idx + 1}</span>
                    <span className="flex-1 text-sm font-semibold text-[var(--brand-ink)]">{p.name}</span>
                    <span
                      className="inline-flex min-w-[2.5rem] justify-center rounded-full bg-[#F0F7FC] px-2.5 py-0.5 text-xs font-bold text-[#002d75]"
                      data-testid={`classic-squad-selection-count-${p.id}`}
                    >
                      {p.selection_count || 0}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Squad list */}
        <div className="rounded-md border border-black/10 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            <User size={16} className="text-[#6CABDD]" />
            Active Classic Squad list
          </h2>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((id) => (
                <div key={id} className="h-16 animate-pulse rounded-md border border-black/10 bg-[#F8FAFC]" />
              ))}
            </div>
          ) : squad.length === 0 ? (
            <p className="p-8 text-center text-sm text-[var(--brand-body)] italic">No Classic squad players yet — add your first legend above.</p>
          ) : (
            <ul className="divide-y divide-black/5">
              {squad.map((player) => (
                <li key={player.id} className="py-4 flex items-center justify-between gap-4 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3">
                    <span className={`h-2.5 w-2.5 rounded-full ${player.position_type === "goalkeeper" ? "bg-emerald-500" : "bg-sky-400"}`} />
                    {player.shirt_design_id ? (
                      <ShirtIcon
                        isGk={false}
                        sizeClass="h-8 w-8"
                        showNumber={false}
                        design={shirtDesigns.find((d) => d.id === player.shirt_design_id) || "classic"}
                        data-testid={`classic-squad-row-shirt-${player.id}`}
                      />
                    ) : null}
                    <div>
                      <p className="font-display font-bold text-sm text-[var(--brand-ink)]">
                        {player.name}
                      </p>
                      <p className="mt-0.5 text-xs text-[var(--brand-body)]">
                        {player.position_type === "goalkeeper" ? "Goalkeeper" : "Outfield Player"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => startEdit(player)}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-black/10 px-3 text-xs font-semibold text-[var(--brand-ink)] hover:border-[#0A0F17] hover:bg-black/5"
                    >
                      <Edit2 size={10} /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(player.id)}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-3 text-xs font-semibold text-[#E63946] hover:bg-[#E63946] hover:text-white"
                    >
                      <Trash2 size={10} /> Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
